import React, {useMemo} from "react";
import {InvoicesListsReportDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {OpenInNew} from "@mui/icons-material";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {ReactComponent as Passed} from "../../../../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../../../../assets/images/validations/failed.svg";
import {CircularProgress, SvgIcon} from "@mui/material";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {ReactComponent as Printer} from '../../../../../../../../../assets/images/printer.svg'
import PartnerIndustryRules from "../../../../../../../../../core/services/partner-industry-rules";
import {usePartnerInformation} from "../../../../../../../../hooks/use-partner-information";
import CacheService from "../../../../../../../../../core/services/cache/cache_service";


const InvoicesListViewReportTable = ({
                                         searchData,
                                         exportToCsv,
                                         data,
                                         dataGridSaveConfig,
                                         fetchingOrdersCustomer,
                                         navigateToInformationView,
                                         navigateToPrintView,
                                     }) => {
    const operatorAccessLevels = useAccessLevels();
    const partnerInformation = usePartnerInformation();
    const partnerId = useMemo(() => partnerInformation?.id ?? CacheService.getOperatorPartnerId(), [partnerInformation?.id],);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.invoiceInformation ? [
                {
                    name: InvoicesListsReportDataGridCellKeys.navigate,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: 0,
                },
                ...(operatorAccessLevels.packages.bizPrint.printInvoice ? [
                    {
                        name: InvoicesListsReportDataGridCellKeys.print,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        pinned: true,
                        sortable: false,
                        pinnedToggleable: false,
                        resizable: false,
                        visibilityToggleable: false,
                        width: 0,
                    },
                ] : []),
            ] : []),
            {
                title: "Invoice No",
                name: InvoicesListsReportDataGridCellKeys.invoiceNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
            },
            {
                title: "Customer",
                name: InvoicesListsReportDataGridCellKeys.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Invoice Date",
                name: InvoicesListsReportDataGridCellKeys.invoiceDate,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
            },
            {
                title: "Customer PO",
                name: InvoicesListsReportDataGridCellKeys.customerPO,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Sales Person",
                name: InvoicesListsReportDataGridCellKeys.salespersonNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Avg Cost",
                name: InvoicesListsReportDataGridCellKeys.avgCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                title: "Discount",
                name: InvoicesListsReportDataGridCellKeys.discountAmount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                title: "Applied Gift Card",
                name: InvoicesListsReportDataGridCellKeys.appliedGiftCardAmount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 150,
            },
            {
                title: "Sub-total",
                name: InvoicesListsReportDataGridCellKeys.subtotal,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 150,
            },
            {
                title: "Total",
                name: InvoicesListsReportDataGridCellKeys.totalOrdered,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 120,
            },
            {
                title: "Balance",
                name: InvoicesListsReportDataGridCellKeys.balance,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 120,
            },
            {
                title: "Status",
                name: InvoicesListsReportDataGridCellKeys.status,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
            },
        ], [operatorAccessLevels.packages.bizPrint.printInvoice, operatorAccessLevels.packages.sales.invoiceInformation])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data?.map(order => ({
            key: order.id,
            data: order,
            cells: {
                [InvoicesListsReportDataGridCellKeys.navigate]:
                    <IconButton
                        color={'secondary'}
                        disabled={fetchingOrdersCustomer.includes(order.id)}
                        onClick={() => navigateToInformationView(order.customer, order.id)}

                    >
                        {
                            fetchingOrdersCustomer.includes(order.id)
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : <OpenInNew className='icon normal'/>
                        }
                    </IconButton>,
                [InvoicesListsReportDataGridCellKeys.print]:
                    <IconButton
                        color={'paper.secondary'}
                        disabled={fetchingOrdersCustomer.includes(order.id)}
                        onClick={() => navigateToPrintView(order.customer, order.id)}
                    >
                        {
                            fetchingOrdersCustomer.includes(order.id)
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : <SvgIcon className='icon medium' component={Printer}/>
                        }

                    </IconButton>,
                [InvoicesListsReportDataGridCellKeys.invoiceNo]: order.invoiceNo,
                [InvoicesListsReportDataGridCellKeys.customer]: order.customer?.name,
                [InvoicesListsReportDataGridCellKeys.invoiceDate]: order.invoiceDate,
                [InvoicesListsReportDataGridCellKeys.customerPO]: order.customerPO,
                [InvoicesListsReportDataGridCellKeys.salespersonNo]: order.salespersonNo,
                [InvoicesListsReportDataGridCellKeys.avgCost]: order.avgCost,
                [InvoicesListsReportDataGridCellKeys.discountAmount]: order.discountAmount,
                [InvoicesListsReportDataGridCellKeys.appliedGiftCardAmount]: order.appliedGiftCardAmount,
                [InvoicesListsReportDataGridCellKeys.subtotal]: order.subTotal,
                [InvoicesListsReportDataGridCellKeys.totalOrdered]: order.total,
                [InvoicesListsReportDataGridCellKeys.balance]: order.balance,
                [InvoicesListsReportDataGridCellKeys.status]:
                    !PartnerIndustryRules.effectiveIsNeededToPay(order, partnerId)
                        ? <Passed/>
                        : <Failed/>
            }
        }))
    }, [data, partnerId, fetchingOrdersCustomer, navigateToInformationView, navigateToPrintView])

    return (
        <>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={dataGridRows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                getExportInfo={exportToCsv}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    );
}

export default InvoicesListViewReportTable;
